import React, { useMemo, useState } from 'react';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  Cell,
  CartesianGrid,
  ComposedChart,
  LabelList,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useTranslation } from 'react-i18next';
import { Card } from '../../../ComponnentsUI/common/Card';
import {
  HomeKpisTraining,
  HomeKpisStructurePoint,
  HomeKpisStructureFacility,
} from '../../../interfaces/homeKpis';

const CENTER_HUES: Record<string, string> = {
  'Moulay Bouazza': '#94A3B8',
  'Aghbala': '#7DD3FC',
  'El Ksiba': '#A5B4FC',
  'Boutferda': '#C4B5FD',
  'Taguelft': '#F9A8D4',
  'Anergui': '#FCA5A5',
  'Ahansal': '#FCD34D',
  'Aït Tamlil': '#86EFAC',
};
const AGHBALA_2ND_KEY = 'Aghbala (2nd cohort)';
const AGHBALA_2ND_FILL = CENTER_HUES['Aghbala'];
const AGHBALA_2ND_OPACITY = 0.55;

type DataSource = 'standard' | 'enhanced';

interface TrainingCenterRow {
  center: string;
  count: number;
}

interface MonthColumn {
  month: string;
  centers: TrainingCenterRow[];
  total: number;
}

const MONTHS: MonthColumn[] = [
  {
    month: 'Apr 2026',
    total: 6,
    centers: [
      { center: 'Moulay Bouazza', count: 2 },
      { center: 'Aghbala', count: 4 },
    ],
  },
  {
    month: 'May 2026',
    total: 5,
    centers: [
      { center: 'El Ksiba', count: 2 },
      { center: 'Boutferda', count: 3 },
    ],
  },
  {
    month: 'Jun 2026',
    total: 11,
    centers: [
      { center: 'Taguelft', count: 2 },
      { center: 'Anergui', count: 3 },
      { center: 'Ahansal', count: 3 },
      { center: 'Aït Tamlil', count: 3 },
    ],
  },
  {
    month: 'Jul 2026',
    total: 5,
    centers: [{ center: AGHBALA_2ND_KEY, count: 5 }],
  },
];

const CENTER_KEYS = Array.from(
  new Set(MONTHS.flatMap((m) => m.centers.map((c) => c.center))),
);

const fillFor = (center: string) => {
  if (center === AGHBALA_2ND_KEY) return AGHBALA_2ND_FILL;
  return CENTER_HUES[center] ?? '#CBD5E1';
};

const opacityFor = (center: string) =>
  center === AGHBALA_2ND_KEY ? AGHBALA_2ND_OPACITY : 1;

const StatTile: React.FC<{
  value: string;
  label: string;
  subtitle?: string;
  valueColor?: string;
}> = ({ value, label, subtitle, valueColor }) => (
  <div className="bg-card-color-100 dark:bg-gray-800 p-5 rounded-xl border border-gray-200 dark:border-gray-700 flex flex-col h-full">
    <div
      className="text-3xl font-bold leading-tight tabular-nums"
      style={{ color: valueColor }}
    >
      {value}
    </div>
    <div className="mt-1 text-xs font-medium text-gray-600 dark:text-gray-300">
      {label}
    </div>
    {subtitle && (
      <div className="mt-1 text-[11px] text-gray-500 dark:text-gray-400">
        {subtitle}
      </div>
    )}
  </div>
);

const MidwivesPerMonthChart: React.FC = () => {
  const chartData = MONTHS.map((m) => {
    const row: Record<string, number | string> = { month: m.month };
    for (const c of m.centers) row[c.center] = c.count;
    row['__total'] = m.total;
    return row;
  });
  return (
    <div className="h-64">
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart
          data={chartData}
          margin={{ top: 24, right: 24, left: -12, bottom: 0 }}
        >
          <CartesianGrid
            strokeDasharray="3 3"
            vertical={false}
            stroke="currentColor"
            className="text-gray-200 dark:text-gray-700"
          />
          <XAxis
            dataKey="month"
            tick={{ fontSize: 12 }}
            stroke="currentColor"
            axisLine={false}
            tickLine={false}
            padding={{ left: 24, right: 24 }}
            className="text-gray-500 dark:text-gray-400"
          />
          <YAxis
            tick={{ fontSize: 11 }}
            stroke="currentColor"
            axisLine={false}
            tickLine={false}
            allowDecimals={false}
            className="text-gray-400"
          />
          <Tooltip
            cursor={{ fill: 'rgba(148, 163, 184, 0.12)' }}
            formatter={(value: number, name: string) => [value, name]}
          />
          <Legend
            wrapperStyle={{ fontSize: 11, paddingTop: 12, lineHeight: '18px' }}
            iconType="circle"
            iconSize={8}
          />
          {CENTER_KEYS.map((center, idx) => (
            <Bar
              key={center}
              dataKey={center}
              stackId="a"
              fill={fillFor(center)}
              fillOpacity={opacityFor(center)}
              name={center}
              radius={idx === CENTER_KEYS.length - 1 ? [4, 4, 0, 0] : 0}
              stroke="white"
              strokeWidth={2}
              maxBarSize={56}
            />
          ))}
          <Line
            dataKey="__total"
            stroke="transparent"
            dot={false}
            activeDot={false}
            legendType="none"
            isAnimationActive={false}
          >
            <LabelList
              dataKey="__total"
              position="top"
              content={(props: any) => {
                const { x, y, value } = props;
                if (value == null) return null;
                return (
                  <text
                    x={x}
                    y={(y ?? 0) - 8}
                    textAnchor="middle"
                    className="text-[12px] font-semibold fill-gray-800 dark:fill-gray-100 tabular-nums"
                  >
                    {value}
                  </text>
                );
              }}
            />
          </Line>
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
};

// -- Detected structures per study ------------------------------------

const TREND_COLOR = '#3B82F6';
const SLOPE_UP_COLOR = '#3B82F6';
const SLOPE_FLAT_COLOR = '#94A3B8';
const AXIS_MUTED = '#94A3B8';
const LOW_N_THRESHOLD = 5;

const ENHANCED_COMBINED: HomeKpisStructurePoint[] = [
  { month: '2026-04', mean: 28.71, sd: 8.41, n: 17 },
  { month: '2026-05', mean: 30.32, sd: 8.18, n: 57 },
  { month: '2026-06', mean: 30.36, sd: 7.01, n: 50 },
  { month: '2026-07', mean: 31.39, sd: 5.59, n: 28 },
];

const ENHANCED_BY_FACILITY: HomeKpisStructureFacility[] = [
  {
    facility_id: null,
    facility_name: 'CSR1 Boutferda',
    total_studies: 9,
    months_shown: 3,
    monthly: [
      { month: '2026-05', mean: 23.6, sd: 7.16, n: 5 },
      { month: '2026-06', mean: 38.33, sd: 0.58, n: 3 },
      { month: '2026-07', mean: 35.0, sd: 0.0, n: 1 },
    ],
  },
  {
    facility_id: null,
    facility_name: 'CSR2 Aghbala',
    total_studies: 44,
    months_shown: 3,
    monthly: [
      { month: '2026-04', mean: 24.56, sd: 9.29, n: 9 },
      { month: '2026-05', mean: 29.58, sd: 9.55, n: 26 },
      { month: '2026-06', mean: 31.0, sd: 8.66, n: 9 },
    ],
  },
  {
    facility_id: null,
    facility_name: 'CSR2 Anergui',
    total_studies: 8,
    months_shown: 2,
    monthly: [
      { month: '2026-06', mean: 37.6, sd: 2.41, n: 5 },
      { month: '2026-07', mean: 36.0, sd: 1.73, n: 3 },
    ],
  },
  {
    facility_id: null,
    facility_name: 'CSR2 Aït Tamlil',
    total_studies: 7,
    months_shown: 2,
    monthly: [
      { month: '2026-06', mean: 28.67, sd: 5.89, n: 6 },
      { month: '2026-07', mean: 29.0, sd: 0.0, n: 1 },
    ],
  },
  {
    facility_id: null,
    facility_name: 'CSR2 Moulay Bouazza',
    total_studies: 40,
    months_shown: 4,
    monthly: [
      { month: '2026-04', mean: 33.38, sd: 4.0, n: 8 },
      { month: '2026-05', mean: 35.2, sd: 5.37, n: 15 },
      { month: '2026-06', mean: 34.0, sd: 2.94, n: 4 },
      { month: '2026-07', mean: 32.31, sd: 4.96, n: 13 },
    ],
  },
  {
    facility_id: null,
    facility_name: 'CSU El Ksiba',
    total_studies: 44,
    months_shown: 3,
    monthly: [
      { month: '2026-05', mean: 28.45, sd: 4.7, n: 11 },
      { month: '2026-06', mean: 27.3, sd: 6.2, n: 23 },
      { month: '2026-07', mean: 28.7, sd: 6.46, n: 10 },
    ],
  },
];

const ENHANCED_TOTALS = {
  total_studies: ENHANCED_COMBINED.reduce((s, m) => s + m.n, 0),
  months_shown: ENHANCED_COMBINED.length,
};

const facilityColor = (name: string): string =>
  FACILITY_COLORS[name] ?? '#94A3B8';

const monthShort = (ym: string): string => {
  const parts = ym.split('-');
  if (parts.length !== 2) return ym;
  const monthNames = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];
  const idx = parseInt(parts[1], 10) - 1;
  const name = monthNames[idx] ?? parts[1];
  return `${name} ${parts[0]}`;
};

const SegmentedToggle: React.FC<{
  value: DataSource;
  onChange: (v: DataSource) => void;
  standardLabel: string;
  enhancedLabel: string;
}> = ({ value, onChange, standardLabel, enhancedLabel }) => (
  <div
    className="inline-flex rounded-full border border-gray-200 dark:border-gray-700 bg-card-color-100 dark:bg-gray-800 p-1"
    role="tablist"
  >
    {(
      [
        ['standard', standardLabel],
        ['enhanced', enhancedLabel],
      ] as const
    ).map(([key, label]) => {
      const selected = value === key;
      return (
        <button
          key={key}
          type="button"
          role="tab"
          aria-selected={selected}
          onClick={() => onChange(key)}
          className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
            selected
              ? 'bg-blue-600 text-white shadow-sm'
              : 'text-gray-600 dark:text-gray-300 hover:bg-card-color-200 dark:hover:bg-gray-700'
          }`}
        >
          {label}
        </button>
      );
    })}
  </div>
);


// Card 2 - Table of first vs latest by facility, sorted by change.

interface FacilityRow {
  facility_name: string;
  first: HomeKpisStructurePoint;
  last: HomeKpisStructurePoint;
  delta: number;
}

const buildFacilityRows = (
  by_facility: HomeKpisStructureFacility[],
): FacilityRow[] => {
  const rows: FacilityRow[] = [];
  for (const fac of by_facility) {
    const monthly = [...(fac.monthly || [])].sort((a, b) =>
      a.month.localeCompare(b.month),
    );
    if (monthly.length === 0) continue;
    const first = monthly[0];
    const last = monthly[monthly.length - 1];
    // A single-month facility has no change to report.
    if (first === last) continue;
    rows.push({
      facility_name: fac.facility_name,
      first,
      last,
      delta: last.mean - first.mean,
    });
  }
  rows.sort((a, b) => b.delta - a.delta);
  return rows;
};

const FacilityChangeTable: React.FC<{
  rows: FacilityRow[];
  facilityLabel: string;
  firstLabel: string;
  latestLabel: string;
  changeLabel: string;
}> = ({ rows, facilityLabel, firstLabel, latestLabel, changeLabel }) => {
  if (!rows.length) return null;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <thead>
          <tr className="text-left text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
            <th className="py-2 pr-3 font-semibold">{facilityLabel}</th>
            <th className="py-2 px-2 font-semibold text-right">{firstLabel}</th>
            <th className="py-2 px-2 font-semibold text-right">{latestLabel}</th>
            <th className="py-2 pl-2 font-semibold text-right">{changeLabel}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const firstLowN = row.first.n < LOW_N_THRESHOLD;
            const lastLowN = row.last.n < LOW_N_THRESHOLD;
            const isUp = row.delta > 0;
            const isFlat = Math.abs(row.delta) < 0.05;
            const changeColor = isFlat
              ? '#6B7280'
              : isUp
                ? '#059669'
                : '#DC2626';
            const arrow = isFlat ? '·' : isUp ? '↑' : '↓';
            const tooltipTitle =
              `${row.facility_name}\n` +
              `${monthShort(row.first.month)} · mean ${row.first.mean.toFixed(2)} / SD ${row.first.sd.toFixed(2)} · n=${row.first.n}${firstLowN ? '*' : ''}\n` +
              `${monthShort(row.last.month)} · mean ${row.last.mean.toFixed(2)} / SD ${row.last.sd.toFixed(2)} · n=${row.last.n}${lastLowN ? '*' : ''}`;
            return (
              <tr
                key={row.facility_name}
                className="border-b border-gray-100 dark:border-gray-800 last:border-0"
                title={tooltipTitle}
              >
                <td className="py-2 pr-3 text-gray-800 dark:text-gray-100 font-medium">
                  {row.facility_name}
                </td>
                <td className="py-2 px-2 text-right tabular-nums text-gray-700 dark:text-gray-200">
                  {row.first.mean.toFixed(1)}
                  {firstLowN ? '*' : ''}
                </td>
                <td className="py-2 px-2 text-right tabular-nums text-gray-700 dark:text-gray-200">
                  {row.last.mean.toFixed(1)}
                  {lastLowN ? '*' : ''}
                </td>
                <td
                  className="py-2 pl-2 text-right tabular-nums font-semibold whitespace-nowrap"
                  style={{ color: changeColor }}
                >
                  <span aria-hidden className="mr-1">
                    {arrow}
                  </span>
                  {row.delta >= 0 ? '+' : '−'}
                  {Math.abs(row.delta).toFixed(1)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

// Trend area chart used inside the top-row card. No error bars.
const TrendAreaChart: React.FC<{
  data: HomeKpisStructurePoint[];
  yMin: number;
  yMax: number;
  denominator: number;
  yAxisLabel: string;
}> = ({ data, yMin, yMax, denominator, yAxisLabel }) => {
  if (!data.length) return null;
  const chartData = data.map((p) => ({
    month: monthShort(p.month).split(' ')[0],
    mean: p.mean,
    sd: p.sd,
    n: p.n,
    ym: p.month,
  }));
  const span = yMax - yMin;
  const step = span >= 20 ? 5 : span >= 10 ? 3 : 2;
  const ticks: number[] = [];
  for (let v = yMin; v <= yMax + 0.001; v += step) ticks.push(v);
  return (
    <div className="h-56 w-full">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart
          data={chartData}
          margin={{ top: 20, right: 20, left: 4, bottom: 4 }}
        >
          <defs>
            <linearGradient id="trendAreaFill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={TREND_COLOR} stopOpacity={0.28} />
              <stop offset="100%" stopColor={TREND_COLOR} stopOpacity={0.05} />
            </linearGradient>
          </defs>
          <CartesianGrid
            strokeDasharray="1 3"
            vertical={false}
            stroke="currentColor"
            strokeOpacity={0.25}
            className="text-gray-400 dark:text-gray-600"
          />
          <XAxis
            dataKey="month"
            tick={{ fontSize: 12, fill: AXIS_MUTED }}
            interval={0}
            axisLine={false}
            tickLine={false}
            padding={{ left: 20, right: 20 }}
          />
          <YAxis
            domain={[yMin, yMax]}
            ticks={ticks}
            tick={{ fontSize: 11, fill: AXIS_MUTED }}
            axisLine={false}
            tickLine={false}
            width={54}
            label={{
              value: yAxisLabel,
              angle: -90,
              position: 'insideLeft',
              style: { fontSize: 10, fill: AXIS_MUTED, textAnchor: 'middle' },
              offset: 8,
            }}
          />
          <Tooltip
            cursor={{ stroke: 'rgba(148, 163, 184, 0.35)' }}
            content={({ active, payload }) => {
              if (!active || !payload || !payload.length) return null;
              const p = payload[0].payload as {
                ym: string;
                mean: number;
                sd: number;
                n: number;
              };
              return (
                <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 shadow-sm text-xs">
                  <div className="font-semibold text-gray-800 dark:text-gray-100 mb-1">
                    {monthShort(p.ym)}
                  </div>
                  <div className="tabular-nums text-gray-700 dark:text-gray-200">
                    mean {p.mean.toFixed(2)} / {denominator}
                  </div>
                  <div className="tabular-nums text-gray-500 dark:text-gray-400">
                    SD {p.sd.toFixed(2)} · n={p.n}
                  </div>
                </div>
              );
            }}
          />
          <Area
            type="monotone"
            dataKey="mean"
            stroke={TREND_COLOR}
            strokeWidth={2}
            fill="url(#trendAreaFill)"
            dot={{ r: 3, fill: TREND_COLOR, strokeWidth: 0 }}
            activeDot={{ r: 5, fill: TREND_COLOR, stroke: 'white', strokeWidth: 2 }}
            isAnimationActive
          >
            <LabelList
              dataKey="mean"
              position="top"
              content={(props: any) => {
                const { x, y, value } = props;
                if (value == null) return null;
                return (
                  <text
                    x={x}
                    y={(y ?? 0) - 8}
                    textAnchor="middle"
                    className="text-[12px] font-semibold fill-gray-800 dark:fill-gray-100 tabular-nums"
                  >
                    {Number(value).toFixed(1)}
                  </text>
                );
              }}
            />
          </Area>
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};

// Facility mini bar chart with ±1 SD error whisker per bar.
const FacilityMini: React.FC<{
  monthly: HomeKpisStructurePoint[];
  yMax: number;
}> = ({ monthly, yMax }) => {
  if (!monthly.length) return null;
  const width = 260;
  const height = 132;
  const leftPad = 6;
  const rightPad = 6;
  const topPad = 16;
  const bottomPad = 30;
  const innerW = width - leftPad - rightPad;
  const innerH = height - topPad - bottomPad;
  const slots = monthly.length;
  const slotW = innerW / slots;
  const barW = Math.min(slotW * 0.55, 32);
  const scaleY = (v: number) => topPad + (1 - v / yMax) * innerH;
  return (
    <div className="w-full">
      <svg
        viewBox={`0 0 ${width} ${height}`}
        preserveAspectRatio="xMidYMid meet"
        className="w-full h-[132px]"
      >
        {[0, 0.25, 0.5, 0.75, 1].map((f, i) => {
          const y = scaleY(f * yMax);
          return (
            <line
              key={i}
              x1={leftPad}
              x2={width - rightPad}
              y1={y}
              y2={y}
              stroke="currentColor"
              strokeOpacity={0.18}
              strokeDasharray="1 3"
              strokeWidth={0.6}
              className="text-gray-500"
            />
          );
        })}
        {monthly.map((p, i) => {
          const cx = leftPad + slotW * (i + 0.5);
          const barX = cx - barW / 2;
          const meanY = scaleY(p.mean);
          const barH = height - bottomPad - meanY;
          const errTop = scaleY(Math.min(yMax, p.mean + p.sd));
          const errBot = scaleY(Math.max(0, p.mean - p.sd));
          const low = p.n < LOW_N_THRESHOLD;
          const opacity = low ? 0.42 : 1;
          const monthLabel = monthShort(p.month).split(' ')[0];
          return (
            <g key={i} opacity={opacity}>
              <rect
                x={barX}
                y={meanY}
                width={barW}
                height={Math.max(0, barH)}
                rx={2}
                fill={TREND_COLOR}
              />
              <text
                x={cx}
                y={meanY - 4}
                textAnchor="middle"
                fontSize={9.5}
                fontWeight={600}
                className="fill-gray-800 dark:fill-gray-100 tabular-nums"
              >
                {p.mean.toFixed(1)}
              </text>
              {p.sd > 0 && (
                <>
                  <line
                    x1={cx}
                    x2={cx}
                    y1={errTop}
                    y2={errBot}
                    stroke="currentColor"
                    strokeOpacity={0.75}
                    strokeWidth={0.8}
                    className="text-gray-700 dark:text-gray-300"
                  />
                  <line
                    x1={cx - barW * 0.28}
                    x2={cx + barW * 0.28}
                    y1={errTop}
                    y2={errTop}
                    stroke="currentColor"
                    strokeOpacity={0.75}
                    strokeWidth={0.8}
                    className="text-gray-700 dark:text-gray-300"
                  />
                  <line
                    x1={cx - barW * 0.28}
                    x2={cx + barW * 0.28}
                    y1={errBot}
                    y2={errBot}
                    stroke="currentColor"
                    strokeOpacity={0.75}
                    strokeWidth={0.8}
                    className="text-gray-700 dark:text-gray-300"
                  />
                </>
              )}
              <text
                x={cx}
                y={height - bottomPad + 12}
                textAnchor="middle"
                fontSize={10}
                className="fill-gray-600 dark:fill-gray-300"
              >
                {monthLabel}
              </text>
              <text
                x={cx}
                y={height - bottomPad + 24}
                textAnchor="middle"
                fontSize={9}
                className="fill-gray-500 dark:fill-gray-400 tabular-nums"
              >
                n={p.n}
                {low ? '*' : ''}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
};

interface StructuresSectionProps {
  training: HomeKpisTraining | undefined;
}

// Shared state hook so both halves of the section stay in sync on the toggle.
const useStructuresState = (training: HomeKpisTraining | undefined) => {
  const [source, setSource] = useState<DataSource>('enhanced');
  const enhancedDenominator = training?.denominator || 56;
  const { combined, byFacility, denominator } = useMemo(() => {
    if (source === 'enhanced') {
      return {
        combined: ENHANCED_COMBINED,
        byFacility: ENHANCED_BY_FACILITY,
        denominator: enhancedDenominator,
      };
    }
    return {
      combined: training?.combined ?? [],
      byFacility: training?.by_facility ?? [],
      denominator: training?.denominator ?? 56,
    };
  }, [source, training, enhancedDenominator]);
  return { source, setSource, combined, byFacility, denominator };
};


interface SectionState {
  source: DataSource;
  setSource: (v: DataSource) => void;
  combined: HomeKpisStructurePoint[];
  byFacility: HomeKpisStructureFacility[];
  denominator: number;
}

const StructuresTrendCard: React.FC<{
  state: SectionState;
  showToggle?: boolean;
}> = ({ state, showToggle }) => {
  const { t } = useTranslation('Dashboard');
  const { combined, denominator } = state;

  const { yMin, yMax } = useMemo(() => {
    if (!combined.length) return { yMin: 0, yMax: 40 };
    const means = combined.map((p) => p.mean);
    const min = Math.min(...means);
    const max = Math.max(...means);
    const span = max - min;
    const padBelow = Math.max(2.5, span * 0.8);
    const padAbove = Math.max(1.5, span * 0.55);
    return {
      yMin: Math.max(0, Math.floor(min - padBelow)),
      yMax: Math.min(denominator, Math.ceil(max + padAbove)),
    };
  }, [combined, denominator]);

  const latest = combined.length ? combined[combined.length - 1] : null;
  const first = combined.length ? combined[0] : null;
  const firstMonthLabel = first ? monthShort(first.month).split(' ')[0] : '';
  const totalExams = combined.reduce((s, p) => s + p.n, 0);
  const meanChangePct = useMemo(() => {
    if (!first || !latest || first.mean === 0) return null;
    return ((latest.mean - first.mean) / first.mean) * 100;
  }, [first, latest]);
  const changeColor =
    meanChangePct === null || Math.abs(meanChangePct) < 0.5
      ? '#475569'
      : meanChangePct > 0
        ? '#047857'
        : '#B91C1C';

  return (
    <Card
      title={t('kpis.training.structures.trendTitle')}
      className="h-full"
      controls={
        showToggle ? (
          <SegmentedToggle
            value={state.source}
            onChange={state.setSource}
            standardLabel={t('kpis.dataSource.standard')}
            enhancedLabel={t('kpis.dataSource.enhanced')}
          />
        ) : undefined
      }
    >
      {combined.length === 0 ? (
        <div className="text-xs text-gray-500 dark:text-gray-400 py-8 text-center">
          {t('kpis.training.structures.empty')}
        </div>
      ) : (
        <>
          <div className="mb-3 flex flex-wrap items-baseline gap-x-6 gap-y-1 text-xs">
            <div>
              <span className="text-base font-bold text-gray-900 dark:text-gray-100 tabular-nums">
                {latest ? latest.mean.toFixed(1) : '-'}
              </span>
              <span className="ml-1.5 text-[11px] text-gray-500 dark:text-gray-400">
                {t('kpis.training.structures.inline.latest')}
              </span>
            </div>
            {meanChangePct !== null && (
              <div>
                <span
                  className="text-base font-bold tabular-nums"
                  style={{ color: changeColor }}
                >
                  {`${meanChangePct >= 0 ? '+' : '−'}${Math.abs(Math.round(meanChangePct))}%`}
                </span>
                <span className="ml-1.5 text-[11px] text-gray-500 dark:text-gray-400">
                  {t('kpis.training.structures.inline.sinceFirst', {
                    firstMonth: firstMonthLabel,
                  })}
                </span>
              </div>
            )}
            <div>
              <span className="text-base font-bold text-gray-900 dark:text-gray-100 tabular-nums">
                {totalExams.toLocaleString()}
              </span>
              <span className="ml-1.5 text-[11px] text-gray-500 dark:text-gray-400">
                {t('kpis.training.structures.inline.totalExams')}
              </span>
            </div>
          </div>
          <TrendAreaChart
            data={combined}
            yMin={yMin}
            yMax={yMax}
            denominator={denominator}
            yAxisLabel={t('kpis.training.structures.yAxisLabel', {
              denominator,
            })}
          />
        </>
      )}
    </Card>
  );
};

const StructuresFacilityGrid: React.FC<{ state: SectionState }> = ({ state }) => {
  const { t } = useTranslation('Dashboard');
  const { byFacility } = state;

  const yMax = useMemo(() => {
    if (!byFacility.length) return 40;
    const highs: number[] = [];
    for (const f of byFacility) {
      for (const p of f.monthly) highs.push(p.mean + p.sd);
    }
    const max = highs.length ? Math.max(...highs) : 40;
    return Math.ceil((max + 1) / 5) * 5;
  }, [byFacility]);

  if (!byFacility.length) return null;

  return (
    <div>
      <div className="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-3">
        {t('kpis.training.structures.byFacilityTitle')}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">
        {byFacility.map((fac) => {
          const totalExams = fac.monthly.reduce((s, p) => s + p.n, 0);
          return (
            <Card key={fac.facility_name} title={fac.facility_name}>
              <div className="mb-2 text-[11px] text-gray-500 dark:text-gray-400">
                {t('kpis.training.structures.facilityMeta', {
                  studies: totalExams,
                  months: fac.monthly.length,
                })}
              </div>
              <FacilityMini monthly={fac.monthly} yMax={yMax} />
            </Card>
          );
        })}
      </div>
    </div>
  );
};

export const TrainingQualityTab: React.FC<{ training?: HomeKpisTraining }> = ({
  training,
}) => {
  const { t } = useTranslation('Dashboard');
  const state = useStructuresState(training);
  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3 sm:gap-6">
        <StatTile value="27" label={t('kpis.training.tiles.midwives')} />
        <StatTile value="8" label={t('kpis.training.tiles.centers')} />
        <StatTile value="4" label={t('kpis.training.tiles.months')} />
      </div>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2 sm:gap-6 items-stretch">
        <Card title={t('kpis.training.cards.perMonth')} className="h-full">
          <MidwivesPerMonthChart />
          <div className="mt-3 text-xs text-gray-500 dark:text-gray-400">
            {t('kpis.training.cumulative')}: 6 → 11 → 22 → 27
          </div>
          <div className="mt-1 text-[11px] text-gray-400 dark:text-gray-500">
            {t('kpis.training.perMonth.aghbalaNote')}
          </div>
        </Card>
        <StructuresTrendCard state={state} showToggle />
      </div>

      <StructuresFacilityGrid state={state} />

      <div className="text-[11px] text-gray-500 dark:text-gray-400">
        {t('kpis.training.structures.footnote', { denominator: state.denominator })}
      </div>
    </div>
  );
};
